import { assert } from '@common/errors';
import { loadVGSCollect } from '@vgs/collect-js';
import React, { useContext, useEffect, useState } from 'react';

/** Partial typedef for VGS Collect Field.
 * Full explanation here: https://www.verygoodsecurity.com/docs/api/collect
 */
interface VGSField {
    on: (event: string, callback: (state: any) => void) => void;
    off: (event: string, callback: (state: any) => void) => void;
    /** https://www.verygoodsecurity.com/docs/api/collect/#api-fieldupdate */
    update: (properties: any) => void;
    /** https://www.verygoodsecurity.com/docs/api/collect/#api-fieldreset */
    reset: () => void;
}

/** Partial typedef for VGS Collect Form.
 * Full explanation here: https://www.verygoodsecurity.com/docs/api/collect
 */
interface VGSForm {
    /** https://www.verygoodsecurity.com/docs/api/collect/#api-formfield */
    field: (selector: string, properties: any) => VGSField;
    /** https://www.verygoodsecurity.com/docs/api/collect/#api-formtokenize */
    tokenize: (
        responseCallback: (status: any, response: any) => void,
        validationCallback: (errors: any) => void
    ) => Promise<void>;
}

type VGSContextType = {
    vgsForm: VGSForm | null;
    tokenizeSSN:
        | (() => Promise<{
              errorMessage: string;
              taxIdToken: string;
          }>)
        | null;
};

const VGSContext = React.createContext<VGSContextType>({
    vgsForm: null,
    tokenizeSSN: null,
});

export const VGSProvider = ({ children }: { children: React.ReactNode }) => {
    const [vgsForm, setVGSForm] = useState<any>(null);

    const tokenizeSSN = async () => {
        assert(vgsForm);
        let errorMessage = '';
        let taxIdToken = '';

        await new Promise<void>((resolve) => {
            vgsForm?.tokenize(
                (_status: unknown, data: any) => {
                    taxIdToken = data.ssn;
                    resolve();
                },
                (errors: any) => {
                    if (errors?.ssn?.errorMessages?.length) {
                        errorMessage = errors.ssn.errorMessages[0];
                        errorMessage =
                            errorMessage === 'is required'
                                ? 'SSN is required'
                                : errorMessage === 'is not a valid ssn'
                                  ? 'SSN is not valid'
                                  : errorMessage;
                    }
                    resolve();
                }
            );
        });

        return {
            errorMessage,
            taxIdToken,
        };
    };

    const doLoadVGSCollect = async () => {
        const collect = (await loadVGSCollect({
            vaultId: process.env.NEXT_PUBLIC_VGS_VAULT_ID ?? '',
            environment: process.env.NEXT_PUBLIC_VGS_ENVIRONMENT ?? '',
            version: '2.20.0',
        })) as any; // TODO: Add typedef

        const form: VGSForm = collect.init();

        setVGSForm(form);
    };

    useEffect(() => {
        doLoadVGSCollect();
    }, []);

    return (
        <VGSContext.Provider value={{ vgsForm, tokenizeSSN }}>
            {children}
        </VGSContext.Provider>
    );
};

export const useVGSContext = () => {
    return useContext(VGSContext);
};
